import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore, usePhase } from "@/state/store";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { VoteTally } from "@offsite/shared";

const VOTING_DURATION_SEC = 2 * 60;

export function VoteTallyBoard() {
  const { state, dispatch } = useStore();
  const phase = usePhase();
  const readOnly = useReadOnly();
  const [tallies, setTallies] = useState<VoteTally[]>([]);
  const [closing, setClosing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [, setTick] = useState(0);

  // 1-second tick so the timer countdown is live.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll topics + tallies every 2 seconds.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [tRes, vRes] = await Promise.all([
          api.topics.list(state.auth.token),
          api.votes.tallies(state.auth.token),
        ]);
        if (cancelled) return;
        dispatch({ type: "topics/set", topics: tRes.topics });
        setTallies(vRes.tallies);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token, dispatch]);

  const topics = state.workshop.topics;
  const tallyByTopic = new Map(tallies.map((t) => [t.topicId, t.count]));
  const totalVotes = useMemo(
    () => tallies.reduce((a, b) => a + b.count, 0),
    [tallies],
  );
  const maxCount = Math.max(1, ...tallies.map((t) => t.count));

  // Sort topics by descending vote count (stable on ties via original index).
  const ranked = useMemo(() => {
    return topics
      .map((t, originalIdx) => ({
        topic: t,
        originalIdx,
        count: tallyByTopic.get(t.id) ?? 0,
      }))
      .sort((a, b) => b.count - a.count || a.originalIdx - b.originalIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, tallies]);

  const winnerTopicId = ranked.length > 0 && ranked[0].count > 0 ? ranked[0].topic.id : null;

  // Timer
  const endsAt = state.workshop.phase.timerEndsAt;
  const remainingSec = endsAt ? Math.max(0, Math.round((endsAt - Date.now()) / 1000)) : null;
  const expired = remainingSec === 0;
  const urgent = remainingSec !== null && remainingSec > 0 && remainingSec <= 15;

  const startTimer = async () => {
    const p = await api.phase.set(state.auth.token, "voting", VOTING_DURATION_SEC);
    dispatch({ type: "phase/set", phase: p });
  };

  const closeVoting = async () => {
    setClosing(true);
    try {
      // Phase → "winner-reveal" zeigt Konfetti + Sieger + Team-Verteilungs-CTA.
      const p = await api.phase.set(state.auth.token, "winner-reveal");
      dispatch({ type: "phase/set", phase: p });
    } finally {
      setClosing(false);
    }
  };

  const doResetVotes = async () => {
    setResetting(true);
    setConfirmReset(false);
    try {
      await api.votes.reset(state.auth.token);
      // Re-set phase=voting + neuer 2-min Timer in einem Aufruf.
      const p = await api.phase.set(state.auth.token, "voting", VOTING_DURATION_SEC);
      dispatch({ type: "phase/set", phase: p });
      const r = await api.votes.tallies(state.auth.token);
      setTallies(r.tallies);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="vote-tally-root">
      <header className="vote-tally-head">
        <div>
          <div className="deck-eyebrow" style={{ marginBottom: 4 }}>
            Live · Abstimmung
          </div>
          <h2 style={{ margin: 0, color: "var(--svo-dunkelblau)" }}>Ergebnis</h2>
        </div>
        {remainingSec !== null ? (
          <div
            className={`vote-tally-timer${expired ? " is-expired" : urgent ? " is-urgent" : ""}`}
          >
            <div className="vote-tally-timer-label">
              {expired ? "Zeit um" : "Verbleibend"}
            </div>
            <div className="vote-tally-timer-clock">
              {String(Math.floor(remainingSec / 60)).padStart(2, "0")}:
              {String(remainingSec % 60).padStart(2, "0")}
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={`btn btn-primary${readOnly ? " is-readonly" : ""}`}
            onClick={startTimer}
            disabled={readOnly}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            Timer starten · 2 min
          </button>
        )}
        <div className="vote-total">
          <b>{totalVotes}</b>
          Stimmen
        </div>
      </header>

      <div className="vote-tally-bars">
        {ranked.length === 0 && (
          <p style={{ color: "var(--svo-grau-500)" }}>
            Noch keine Themen veröffentlicht.
          </p>
        )}
        {ranked.map((r, rank) => {
          const widthPct = (r.count / maxCount) * 100;
          const isWinner = r.topic.id === winnerTopicId;
          const totalPct =
            totalVotes === 0 ? 0 : Math.round((r.count / totalVotes) * 100);
          return (
            <div
              key={r.topic.id}
              className={`vote-bar${isWinner ? " is-winner" : r.count > 0 ? " is-leader" : ""}`}
              style={{ order: rank }}
            >
              <div className="vote-bar-rank">{rank + 1}</div>
              <div className="vote-bar-body">
                <div className="vote-bar-titlerow">
                  <h4 className="vote-bar-title">{r.topic.title}</h4>
                  <div className="vote-bar-count">
                    <strong>{r.count}</strong>
                    <span className="vote-bar-pct">
                      {totalPct}%
                    </span>
                  </div>
                </div>
                <div className="vote-bar-track">
                  <div
                    className="vote-bar-fill"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="vote-foot">
        <div className="vote-total">
          <b>{totalVotes}</b>
          Stimmen insgesamt
        </div>
        <div className="vote-actions">
          <button
            type="button"
            className={`btn btn-ghost${readOnly ? " is-readonly" : ""}`}
            onClick={() => setConfirmReset(true)}
            disabled={resetting || readOnly}
            title={
              readOnly
                ? READ_ONLY_TITLE
                : "Löscht alle Stimmen + startet neuen 2-min-Timer für alle Teilnehmer"
            }
          >
            {resetting ? "…" : "↺ Stimmen zurücksetzen"}
          </button>
          {phase === "voting" && (
            <button
              type="button"
              className={`btn btn-success${readOnly ? " is-readonly" : ""}`}
              onClick={closeVoting}
              disabled={closing || totalVotes === 0 || readOnly}
              style={{ opacity: totalVotes === 0 ? 0.5 : 1 }}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            >
              {closing ? "…" : "→ Abstimmung beenden"}
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Alle Stimmen zurücksetzen?"
        message={
          "Damit wird die Abstimmung komplett geleert. Alle Teilnehmer-Handys " +
          "wechseln innerhalb von 3 Sekunden zurück zur Auswahl-Ansicht und " +
          "können erneut abstimmen.\n\nDer Timer startet neu (2 Minuten)."
        }
        confirmLabel="Ja, zurücksetzen"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doResetVotes}
        onCancel={() => setConfirmReset(false)}
        busy={resetting}
      />
    </div>
  );
}
